import { assertNever } from '@eversports/utility-types'
import { getGoogleSearchParam } from '../../lib/get-google-search-param'

import {
  CustomPaymentMethodType,
  NEW_CARD,
  PaymentAPI,
  PaymentError,
  PaymentMethodStripeSourceTypeMapping,
  PaymentMethodType,
  PaymentMethodVerificationMethod,
  PaymentProvider,
  PaymentResponse,
  PaymentState,
  ProcessPaymentOptions,
} from '../../typings/Payment'

import { ProcessPaymentFunction } from './../../data/context'
import confirmOrder from './confirm-order'
import confirmPayment from './confirm-payment'
import pollPayment from './poll-payment'
import preparePayment from './prepare-payment'

const mapVerifyWithToPaymentMethod = (verifyWith: PaymentMethodVerificationMethod) => {
  switch (verifyWith) {
    case PaymentMethodVerificationMethod.PAYMENT_METHOD_IDEAL:
      return { type: PaymentMethodType.PAYMENT_METHOD_IDEAL, saveCard: [] }
    case PaymentMethodVerificationMethod.PAYMENT_METHOD_SOFORT:
      return { type: PaymentMethodType.PAYMENT_METHOD_SOFORT, saveCard: [] }
    default:
      return assertNever(verifyWith)
  }
}

const processPayment: ProcessPaymentFunction = async (
  paymentProvider,
  paymentAPI,
  options,
): Promise<PaymentResponse> => {
  if (!paymentProvider.stripe) {
    throw new Error('Stripe was not correctly loaded')
  }

  // add the timestamp of the last payment attempt to be able to
  // - detect payment state transitions to the same state as before (f.e.: FAILED => FAILED)
  const lastPaymentAttempt = new Date()

  const { orderId, selectedPaymentMethod } = options

  if (!selectedPaymentMethod.type) {
    throw new Error('Payment function was called without a selected payment method type')
  }

  switch (selectedPaymentMethod.type) {
    case CustomPaymentMethodType.PAYMENT_METHOD_OFFLINE:
      return confirmOrder(paymentAPI, orderId)
    case PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE:
      if (selectedPaymentMethod.bankAccountHolder || selectedPaymentMethod.pspPaymentMethodId) {
        return handleSepaPayment(paymentProvider, paymentAPI, options, lastPaymentAttempt)
      }
      if (options.verifyWith) {
        return handleSourcePayment(
          paymentProvider,
          paymentAPI,
          { ...options, selectedPaymentMethod: mapVerifyWithToPaymentMethod(options.verifyWith) },
          lastPaymentAttempt,
        )
      }
      throw new Error('Unexpected SEPA payment. Use one of (existing payment method/new bank account/verifyWith) ')
    case PaymentMethodType.PAYMENT_METHOD_PAYPAL:
    case PaymentMethodType.PAYMENT_METHOD_BANCONTACT:
    case PaymentMethodType.PAYMENT_METHOD_IDEAL:
    case PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD:
    case PaymentMethodType.PAYMENT_METHOD_GIROPAY:
    case PaymentMethodType.PAYMENT_METHOD_EPS:
    case PaymentMethodType.PAYMENT_METHOD_TWINT:
    case PaymentMethodType.PAYMENT_METHOD_SOFORT: {
      const { error, data } = await preparePayment(paymentAPI, {
        paymentId: options.payment.id,
        paymentMethodType: selectedPaymentMethod.type,
      })

      const clientSecret = data?.paymentIntent?.clientSecret
      if (!clientSecret) {
        throw new Error('no client secret was returned')
      }
      if (error) {
        return { wasSuccessful: false, error }
      }
      const { error: stripeError } = await confirmPaymentIntent(
        paymentProvider,
        { ...options, clientSecret },
        lastPaymentAttempt,
      )
      if (stripeError) {
        return { wasSuccessful: false, error: stripeError.message }
      }
      return pollPayment(paymentAPI, { orderId, lastPaymentAttempt })
    }
    case PaymentMethodType.PAYMENT_METHOD_MULTIBANCO:
      throw new Error('Multibanco payment method is not supported')
    default:
      return assertNever(selectedPaymentMethod.type)
  }
}

const confirmPaymentIntent = async (
  paymentProvider: PaymentProvider,
  {
    clientSecret,
    user,
    selectedPaymentMethod,
    cardElement,
    idealBankElement,
  }: Omit<ProcessPaymentOptions, 'payment'> & { clientSecret: string },
  lastPaymentAttempt: Date,
) => {
  const { stripe } = paymentProvider
  if (!stripe) {
    throw new Error('Stripe provider was not found')
  }

  const createPaymentMethodOptions = <T, P>(options: T, paymentMethod: P = {} as P) => ({
    ...options,
    payment_method: {
      billing_details: {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
      },
      ...paymentMethod,
    },
  })

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD) {
    const options = { save_payment_method: Boolean(selectedPaymentMethod.saveCard.length) }

    // If the user selected a saved card
    if (selectedPaymentMethod.cardId && selectedPaymentMethod.cardId !== NEW_CARD) {
      return stripe.confirmCardPayment(clientSecret, { ...options, payment_method: selectedPaymentMethod.cardId })
    }

    // If the user is using a new card
    if (cardElement) {
      return stripe.confirmCardPayment(clientSecret, createPaymentMethodOptions(options, { card: cardElement }))
    }
  }

  const returnUrlObject = {
    return_url: `${window.location.href}&paymentInProgress=true&lastPaymentAttempt=${lastPaymentAttempt.getTime()}`,
  }

  const trackingParam = getGoogleSearchParam()

  if (trackingParam) {
    const [trackingName, trackingValue] = trackingParam.split('=')
    const returnToAsURL = new URL(returnUrlObject.return_url)
    returnToAsURL.searchParams.append(trackingName, trackingValue)
    returnUrlObject.return_url = returnToAsURL.toString()
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_BANCONTACT) {
    return stripe.confirmBancontactPayment(clientSecret, createPaymentMethodOptions(returnUrlObject))
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_IDEAL && idealBankElement) {
    return stripe.confirmIdealPayment(
      clientSecret,
      createPaymentMethodOptions(returnUrlObject, {
        ideal: idealBankElement,
      }),
    )
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_PAYPAL) {
    return stripe.confirmPayPalPayment(clientSecret, createPaymentMethodOptions(returnUrlObject))
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_SOFORT && selectedPaymentMethod.countryIsoCode) {
    return stripe.confirmSofortPayment(
      clientSecret,
      createPaymentMethodOptions(returnUrlObject, {
        sofort: {
          country: selectedPaymentMethod.countryIsoCode,
        },
      }),
    )
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_EPS) {
    return stripe.confirmEpsPayment(clientSecret, createPaymentMethodOptions(returnUrlObject))
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_GIROPAY) {
    return stripe.confirmGiropayPayment(clientSecret, createPaymentMethodOptions(returnUrlObject))
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_TWINT) {
    return (stripe as unknown as { confirmTwintPayment: (typeof stripe)['confirmPayPalPayment'] }).confirmTwintPayment(
      clientSecret,
      createPaymentMethodOptions(returnUrlObject),
    )
  }

  throw new Error('Payment method not implemented')
}

const handleSourcePayment = async (
  paymentProvider: PaymentProvider,
  paymentAPI: PaymentAPI,
  { orderId, selectedPaymentMethod, user, payment: { paymentIntent } }: ProcessPaymentOptions,
  lastPaymentAttempt: Date,
): Promise<PaymentResponse> => {
  if (!paymentIntent) {
    throw new Error('Payment intent was not correctly defined')
  }

  const fullName = `${user.firstName} ${user.lastName}`

  const returnUrl = new URL(
    `${window.location.href}&paymentInProgress=true&lastPaymentAttempt=${lastPaymentAttempt.getTime()}`,
  )

  const trackingParam = getGoogleSearchParam()

  if (trackingParam) {
    const [trackingName, trackingValue] = trackingParam.split('=')
    returnUrl.searchParams.append(trackingName, trackingValue)
  }

  const { error, source } = await paymentProvider.stripe!.createSource({
    type: PaymentMethodStripeSourceTypeMapping[selectedPaymentMethod.type! as PaymentMethodType],
    owner: {
      name: fullName,
      email: user.email,
    },
    metadata: {
      paymentIntent: paymentIntent.id,
    },
    amount: paymentIntent.amount,
    currency: paymentIntent.currency,
    redirect: {
      return_url: returnUrl.toString(),
    },
  })

  if (error) {
    return { wasSuccessful: false, error: error.message }
  }

  if (!source) {
    return { wasSuccessful: false, error: PaymentError.PAYMENT_ERROR_STRIPE_SOURCE }
  }

  // @ts-ignore 'flow' doesn't seem to exist in the source typing?
  const { flow, redirect, status } = source

  if (redirect && status === 'pending' && flow === 'redirect') {
    window.location.assign(redirect.url)

    // As window.location does the redirect in a non blocking way we do not
    // resolve this Promise and keep the UI in a loading state
    // eslint-disable-next-line promise/param-names
    return new Promise((_resolve, _reject) => {})
  }
  return pollPayment(paymentAPI, { orderId, lastPaymentAttempt })
}

const handleSepaPayment = async (
  paymentProvider: PaymentProvider,
  paymentAPI: PaymentAPI,
  {
    orderId,
    selectedPaymentMethod,
    user,
    ibanElement,
    payment: { paymentIntent, id: paymentId },
  }: ProcessPaymentOptions,
  lastPaymentAttempt: Date,
): Promise<PaymentResponse> => {
  if (!paymentIntent) {
    throw new Error('Payment intent was not correctly defined')
  }

  let { pspPaymentMethodId } = selectedPaymentMethod

  if (selectedPaymentMethod.bankAccountHolder) {
    if (!ibanElement) {
      throw new Error('Iban element was not correctly defined for a new iban payment')
    }

    const { source: createdSource, error } = await paymentProvider.stripe!.createSource(ibanElement, {
      type: 'sepa_debit',
      currency: paymentIntent.currency,
      owner: {
        // TODO: remove empty string props and maybe also null
        name: selectedPaymentMethod.bankAccountHolder,
        email: user.email,
      },
      // @ts-ignore 'mandate' doesn't seem to exist in the typings?
      mandate: {
        notification_method: 'email',
      },
      metadata: {
        paymentIntent: paymentIntent.id,
      },
    })

    if (error) {
      return { wasSuccessful: false, error: error.message }
    }

    if (!createdSource) {
      return { wasSuccessful: false, error: PaymentError.PAYMENT_ERROR_STRIPE_SOURCE }
    }

    pspPaymentMethodId = createdSource.id
  }

  if (!pspPaymentMethodId) {
    throw new Error('No existing bank account')
  }

  const confirmPaymentResult = await confirmPayment(paymentAPI, { pspPaymentMethodId, paymentId })
  if (confirmPaymentResult.__typename === 'ExpectedErrors') {
    return { wasSuccessful: false, error: confirmPaymentResult.errors[0].message }
  }

  // the payment state can either transition to
  // - `ON_HOLD` if a mandate was created initially or to
  // - `IN_PROGRESS` if the a verified mandate is charge immediately
  return pollPayment(paymentAPI, {
    orderId,
    lastPaymentAttempt,
    expectedPaymentStates: [PaymentState.PAYMENT_STATE_ON_HOLD, PaymentState.PAYMENT_STATE_IN_PROGRESS],
  })
}

export default processPayment
